import { UserServiceClient } from 'fitbeat.api-clients';
import { UserProfile } from 'fitbeat.gym-core';
import { BaseUserProfile } from 'fitbeat.models';
import { HeartRateStep , MassUnit, MetricToImperialConverter} from 'fitbeat.models';
import { FormKeyboardSpacer } from 'fitbeat.react-native-components';
import moment from 'moment-timezone';
import React from 'react';
import { translate } from 'react-i18next';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { connect } from 'react-redux';
import { change, Field, reduxForm } from 'redux-form';
import { UnitMeasurementSystemSelection } from '../../../src/Nutrition';
import { textInputDarkStyle } from '../../auth/SignupFormsStyles';
import { Button, ButtonTypes } from '../../components';
import { getApiConfig } from '../../config';
import Theme, { ThemeType } from '../../theme';
import { DatePicker, GenderDropdown, HeightField, WeightField } from '../../ui';
import {
  AuthTokenBuilder, isDateOlderThan16, isRequired,
  isValidDate, normalizeDateDDMMYYYY, ResponsiveLayout,
} from '../../utils';
import { userUpdatedMetricInfo } from '../actions';
import { themeStyleMapper } from './HeartRateMonitorSetup';
import { defaultScale } from '../../utils/scaling';

interface IProps {
  themeType: ThemeType;
  userProfile: UserProfile;
  handleSubmit: any;
  goToStep(step: HeartRateStep): void;
  closeModal(): void;
  updateField(field: string, data: any): void;
  t(key: string): string;
  initialize(userProfile: any): void;
  userUpdatedMetricInfo(userProfile: UserProfile): void;
}

interface IState {
  isLoading: boolean;
  dropDownWidth: number;
  useImperialUnit: boolean;
}

class CaptureMetricInfoComponent extends React.PureComponent<IProps, IState>  {
  public state = {
    isLoading: false,
    dropDownWidth: 0,
    useImperialUnit: false,
  };

  private userServiceClient = new UserServiceClient(getApiConfig(), fetch, console.log, new AuthTokenBuilder());

  constructor(props: IProps) {
    super(props);
    const dob = props.userProfile.dateOfBirth;
    const ddmmyyyy = dob ? moment(dob, 'YYYY-MM-DD').format('DD-MM-YYYY') : '';
    const { initialize, userProfile } = props;
    const height = userProfile.height ? userProfile.height.toString() : '';
    let weight = userProfile.weight ? userProfile.weight.toString() : '';
    let heightImperial = {inches: '', feet: ''};
    if (userProfile.useImperialUnit) {
      this.state.useImperialUnit = userProfile.useImperialUnit;
      if(weight){
        weight= MetricToImperialConverter.convertAmount(MassUnit.Killogram,
          MassUnit.Pound, userProfile.weight).toString();
      }
      if (userProfile.height) {
        heightImperial = MetricToImperialConverter.convertCentimetersToFeetAndInches(userProfile.height);
      }
    }
    initialize({
      dateOfBirth: ddmmyyyy,
      gender: userProfile.gender,
      height,
      weight,
      heightImperial: JSON.stringify(heightImperial),
      useImperialUnit: userProfile.useImperialUnit,
    });
  }
  public setUnitSystemPreference = (useImperialUnit) => {
    this.setState({useImperialUnit});
    this.props.updateField('height', '');
    this.props.updateField('weight', '');
    this.props.updateField('heightImperial', JSON.stringify({inches: '', feet: ''}));
  }
  public saveUserMetricInfo = async (values: any) => {
    const { userProfile, goToStep } = this.props;
    const userProfileCopy = Object.assign({}, userProfile);
    userProfileCopy.dateOfBirth = moment(values.dateOfBirth, 'DD-MM-YYYY').format('YYYY-MM-DD');
    userProfileCopy.gender = values.gender;
    userProfileCopy.height = parseFloat(values.height);
    userProfileCopy.weight = parseFloat(values.weight);
    userProfileCopy.useImperialUnit = false;
    if (this.state.useImperialUnit) {
      const parsedHeightObj = JSON.parse(values.heightImperial);
      userProfileCopy.height=MetricToImperialConverter.convertInchesAndFeetToCentimeters(parsedHeightObj)
      userProfileCopy.weight = MetricToImperialConverter.convertAmount(MassUnit.Pound,
             MassUnit.Killogram, userProfileCopy.weight);
      userProfileCopy.useImperialUnit = true;
    }
    this.setState({ isLoading: true });
    const updatedUserProfile = await this.userServiceClient.updateUser(userProfileCopy);
    this.props.userUpdatedMetricInfo(updatedUserProfile);
    this.setState({ isLoading: false });
    goToStep(HeartRateStep.CAPTURE_BELT);
  }

  public handleBack = () => {
    const { userProfile, goToStep, closeModal } = this.props;
    if (userProfile.heartRateMonitorIdentifier) {
      goToStep(HeartRateStep.HAS_BELT);
    } else {
      closeModal();
    }
  }

  public onDateChangeWeb = (_: Event, dateOfBirthString: string) => {
    this.onDateChange(dateOfBirthString);
  }

  public onDateChange = (dateOfBirthString: string) => {
    const dateOfBirth = moment(dateOfBirthString, 'DD-MM-YYYY').toDate();
    this.setState({
      isMinor: BaseUserProfile.isMinor(dateOfBirth),
      isUnderAge: BaseUserProfile.isUnderAge(dateOfBirth),
    });
  }

  public render() {
    const { t, handleSubmit, themeType = ThemeType.light} = this.props;
    const themeStyle = themeStyleMapper[themeType];
    const { isLoading } = this.state;
    let dateOfBirthStyle;

    let genderStyle: any = { controlStyle: styles.genderDropdownControlStyle };
    if (themeType === ThemeType.dark) {
      dateOfBirthStyle = {
        ...textInputDarkStyle,
        innerContainerStyle: { backgroundColor: Theme.GREY_MEDIUM },
        inputStyle: {color: Theme.WHITE },
      };
      genderStyle = {
        controlStyle: {
          ...styles.genderDropdownControlStyle,
          backgroundColor: Theme.GREY_MEDIUM,
        },
        useTransparentTheme: true,
        controlTextStyle: { color: Theme.WHITE },
      };
    }

    return (
      <View style={styles.container}>
        <FormKeyboardSpacer>
          <ScrollView
            keyboardShouldPersistTaps='handled'
            contentContainerStyle={{ flexGrow: 1 }}>
            <View style={styles.container}>
              <ResponsiveLayout
                col={Platform.OS === 'web' ? 1 : 2}
                thresholdSize='medium'>
                <View>
                  <Text style={[styles.label, themeStyle.textColor1]}>{t('whenWereYouBorn')}</Text>
                  <Field
                    forwardRef
                    isRequired
                    confirmText='Confirm'
                    disableMinAgeRestriction
                    name='dateOfBirth'
                    showLabel={false}
                    onChange={Platform.OS === 'web' ? this.onDateChangeWeb : this.onDateChange}
                    component={DatePicker}
                    normalize={normalizeDateDDMMYYYY}
                    validate={[isValidDate, isRequired, isDateOlderThan16]}
                    placeholder='Date of birth'
                    {...dateOfBirthStyle}
                  />
                </View>
                <View
                  onLayout={(e) => this.setState({ dropDownWidth: e.nativeEvent.layout.width })}>
                  <Text style={[styles.label, themeStyle.textColor1]}>{t('selectGender')}</Text>
                  <GenderDropdown rowWidth={this.state.dropDownWidth}
                    optionsStyle={styles.genderDropdownOptionsStyle}
                    showLabel={false}
                    {...genderStyle}
                  />
                </View>
                <View>
                  <Text style={[styles.label, themeStyle.textColor1]}>{t('howTallAreYou')}</Text>
                  <HeightField {...themeType === ThemeType.dark && textInputDarkStyle }
                  useImperialUnit={this.state.useImperialUnit}
                  />
                </View>
                <View>
                  <Text style={[styles.label, themeStyle.textColor1]}>{t('weightInputLabel')}</Text>
                  <WeightField {...themeType === ThemeType.dark && textInputDarkStyle}
                  useImperialUnit={this.state.useImperialUnit}
                  />
                </View>
                <View style={{paddingTop: 10}}>
                <Text style={[styles.label,
                  themeStyle.textColor1, {marginBottom: 10}]}>{t('selectUnitPreference')}</Text>
                <UnitMeasurementSystemSelection
                    inactiveStyle={themeStyle.container1}
                    containerStyle={{flexDirection: 'row' }}
                    optionInfoContainer={{flex: 0}}
                    selectedUnitSystemImperial={this.state.useImperialUnit}
                    setUnitSystemPreferenceToImperial={() => {
                    this.setUnitSystemPreference(true);
                   }}
                    setUnitSystemPreferenceToMetric={() => {
                    this.setUnitSystemPreference(false);
                     }}
                    optionTextStyle={[{fontSize: defaultScale(15), marginBottom: 0}, themeStyle.textColor1]}
                  />
                </View>
                <View style={{width: 1}}/>
              </ResponsiveLayout>
            </View>
          </ScrollView>
          <View style={styles.buttonContainer}>
            {isLoading ?
              <ActivityIndicator
                size='small'
                color={Theme.PRIMARY}
                animating={true}
              />
              :
              <>
                <Button
                  title={'Back'}
                  isLoading={isLoading}
                  onPress={this.handleBack}
                  fontColor={Theme.PRIMARY}
                  buttonType={ButtonTypes.OUTLINE}
                  containerStyle={styles.backButton}
                />
                <Button
                  title={'Add'}
                  disabled={false}
                  isLoading={isLoading}
                  onPress={handleSubmit(this.saveUserMetricInfo)}
                  fontColor={Theme.WHITE}
                  buttonType={ButtonTypes.NORMAL}
                  containerStyle={styles.addButton}
                />
              </>
            }
          </View>
        </FormKeyboardSpacer>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
  },
  label: {
    ...Theme.textStyle.bodyCopyMed,
    paddingTop: defaultScale(10),
    color: Theme.BLACK,
    fontFamily: Theme.Custom600,
  },
  description: {
    ...Theme.textStyle.bodyCopyReg,
    color: Theme.GREY_MEDIUM,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginVertical: defaultScale(15),
  },
  backButton: {
    flex: 0.3,
    marginRight: defaultScale(5),
  },
  addButton: {
    flex: 1,
  },
  genderDropdownOptionsStyle: {
    position: 'absolute',
    right: 0,
  },
  genderDropdownControlStyle: {
    position: 'relative',
  },
});

const mapDispatchToProps = (dispatch: any): Partial<IProps> => {
  return {
    userUpdatedMetricInfo: (userProfile: UserProfile) => dispatch(userUpdatedMetricInfo(userProfile)),
    updateField: (field, data) => dispatch(change( 'captureMetricInfo', field, data )),
  };
};

export const CaptureMetricInfo =
  reduxForm({ form: 'captureMetricInfo' ,destroyOnUnmount:false})
    (translate(['auth'], { wait: true })
      (connect(undefined, mapDispatchToProps)(CaptureMetricInfoComponent)));
